import { setHomeDashboard, setHomeFailureMessage } from '@/services/set-home-dashboard.service';
import type { UserPreferenceMetadata } from '@/lib/userPreferences';
import type { HomeSurveyConfigPayload } from '@/types';
import type { User } from '@supabase/supabase-js';
import { useCallback, useState } from 'react';

export type UseSetHomeDashboardFlowOptions = {
  user: User | null;
  config: HomeSurveyConfigPayload | null;
  onError: (message: string) => void;
  refreshUserPreferences: () => Promise<void>;
  applyPreferenceMetadata: (metadata: UserPreferenceMetadata) => void;
};

export type ConfirmSetHomeResult = { ok: true } | { ok: false };

export function useSetHomeDashboardFlow({
  user,
  config,
  onError,
  refreshUserPreferences,
  applyPreferenceMetadata,
}: UseSetHomeDashboardFlowOptions) {
  const [busy, setBusy] = useState(false);

  const confirmSetHome = useCallback(async (): Promise<ConfirmSetHomeResult> => {
    if (!config || !user) return { ok: false };
    setBusy(true);
    try {
      const result = await setHomeDashboard(config);
      if (result.metadata) {
        applyPreferenceMetadata(result.metadata);
      }
      if (result.supabase) {
        await refreshUserPreferences();
      }
      if (!result.ok) {
        onError(setHomeFailureMessage(result));
        return { ok: false };
      }
      return { ok: true };
    } finally {
      setBusy(false);
    }
  }, [
    config,
    user,
    onError,
    refreshUserPreferences,
    applyPreferenceMetadata,
  ]);

  return { busy, confirmSetHome };
}
